import { useFormik } from 'formik';
import { Box, Button, Stack, Link, TextField, Typography } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Layout as AuthLayout } from 'layouts/auth/Layout';
import { ChangePasswordValidationSchema } from 'utils/validation';
import { changePassword } from 'store/actions/authActions';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';

const ResetPassword = () => {
  const { resetToken } = useParams();
  const dispatch = useDispatch();
  const auth = useSelector((store: any) => store.auth);
  const navigate = useNavigate();

  useEffect(() => {
    if (auth.authInfo?.passwordChangeConfirmed) {
      navigate('/auth/login');
    }
    if (auth.authInfo.isAuthorized) {
      navigate('/');
    }
  }, [navigate, auth.authInfo?.isAuthorized, auth.authInfo?.passwordChangeConfirmed]);

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
      submit: null,
    },
    validationSchema: ChangePasswordValidationSchema,
    onSubmit: async (values, helpers) => {
      try {
        if (values.password !== values.confirmPassword) {
          helpers.setErrors({ submit: 'Please make sure your passwords match' });
          return;
        }

        dispatch(
          changePassword({
            password: values.password,
            confirmPassword: values.confirmPassword,
            resetToken,
          }),
        );
      } catch (err: any) {
        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: err.message });
        helpers.setSubmitting(false);
      }
    },
  });

  return (
    <AuthLayout>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          flex: '1 1 auto',
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            maxWidth: 550,
            px: 3,
            py: '100px',
            width: '100%',
          }}
        >
          <div>
            <Stack spacing={1} sx={{ mb: 3 }}>
              <Typography variant='h4'>New password</Typography>
            </Stack>
            <form noValidate onSubmit={formik.handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  error={!!(formik.touched.password && formik.errors.password)}
                  fullWidth
                  helperText={formik.touched.password && formik.errors.password}
                  label='Password'
                  name='password'
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  type='password'
                  value={formik.values.password}
                />
                <TextField
                  error={!!(formik.touched.confirmPassword && formik.errors.confirmPassword)}
                  fullWidth
                  helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                  label='Confirm Password'
                  name='confirmPassword'
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  type='password'
                  value={formik.values.confirmPassword}
                />
              </Stack>
              {formik.errors.submit && (
                <Typography color='error' sx={{ mt: 3 }} variant='body2'>
                  {formik.errors.submit}
                </Typography>
              )}
              <LoadingButton
                disabled={auth.authInfo?.isAPIInProgress}
                loading={auth.authInfo?.isAPIInProgress}
                fullWidth
                size='large'
                sx={{ mt: 3 }}
                type='submit'
                variant='contained'
              >
                Reset
              </LoadingButton>
              <Link width={'100%'} href='/auth/login'>
                <Button fullWidth size='large' sx={{ mt: 3 }} variant='contained'>
                  Back
                </Button>
              </Link>
            </form>
          </div>
        </Box>
      </Box>
    </AuthLayout>
  );
};

export default ResetPassword;
